import { useWindowResize } from '@helpers';
import * as d3 from 'd3';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { MultiLineChartAxes } from './components/multi-line-chart-axes/MultiLineChartAxes';
import { MultiLineChartLegend } from './components/multi-line-chart-legend/MultiLineChartLegend';
import { MultiLineChartLinePlot } from './components/multi-line-chart-line-plot/MultiLineChartLinePlot';
import { MultiLineChartOverlay } from './components/multi-line-chart-overlay/MultiLineChartOverlay';
import { MultiLineChartTooltip } from './components/multi-line-chart-tooltip/MultiLineChartTooltip';
import styles from './MultiLineChart.module.scss';
import {
  MultiLineChartLine,
  TooltipData,
  TooltipSort,
  TsPoint,
} from './MultiLineChart.types';
import { drawThresholdFill } from './utils';

const THRESHOLD = 0;

const MultiLineChartFormatters = {
  percent2f: (v: number) => `${d3.format('.2f')(v)}%`,
  percent: (v: number) => `${d3.format('.0f')(v)}%`,
  default: (v: number) => v.toString(),
};

export type MultiLineChartProps = {
  /** Lines to plot. Each carries its own colour, label, and time-series points. */
  lines: MultiLineChartLine[];
  /** Show the hover tooltip with per-line values. Defaults to `false`. */
  hasTooltip?: boolean;
  /** Render the legend strip listing each line. Defaults to `false`. */
  hasLegend?: boolean;
  /** Format used for y-axis tick labels — `'percent2f'` (`12.34%`), `'percent'` (`12%`), or `'default'` (raw). */
  yAxisFormatType?: keyof typeof MultiLineChartFormatters;
  /** Format used for values inside the tooltip. Same options as `yAxisFormatType`. */
  tooltipFormatType?: keyof typeof MultiLineChartFormatters;
  /** Sort order applied to lines inside the tooltip (e.g. by current value, asc/desc). */
  tooltipSort?: TooltipSort;
  /** Optional series used to drive a two-color threshold fill under the chart (paired with `fillColors`). */
  thresholdData?: TsPoint[];
  /** Fill colors for `thresholdData` above and below `0`. */
  fillColors?: {
    positive: string;
    negative: string;
  };
  /** Show the per-line breakdown layout in the tooltip. */
  showBreakdown?: boolean;
  /** String used in the tooltip when a line has no value at the hovered date. Defaults to `'N/A'`. */
  notAvailableLabel?: string;
  /** Locale string forwarded to the x-axis date formatter. */
  locale?: string;
};

/**
 * Multi-series time-series line chart. Renders one or more lines on a
 * shared time axis with optional hover tooltip, legend, and a separate
 * threshold-fill series that paints positive vs negative regions
 * (e.g. relative-strength bands).
 *
 * Resizes responsively to its container; line plots run on SVG and the
 * threshold fill on an inline `<canvas>` for performance.
 */
export const MultiLineChart = ({
  lines,
  hasTooltip = false,
  hasLegend = false,
  yAxisFormatType = 'default',
  tooltipFormatType = 'default',
  tooltipSort,
  thresholdData,
  fillColors,
  showBreakdown,
  notAvailableLabel = 'N/A',
  locale,
}: MultiLineChartProps) => {
  const containerRef = useRef<HTMLElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { width: w, height: h } = useWindowResize({ targetRef: containerRef });
  const [tooltipData, setTooltipData] = useState<TooltipData>();

  const MARGIN = { top: 30, right: 40, bottom: 30, left: 0 };
  const chartWidth = w - MARGIN.right - MARGIN.left;
  const chartHeight = h - MARGIN.top - MARGIN.bottom;

  const mergedData = lines.reduce((acc, line) => {
    return acc.concat(line.data);
  }, [] as TsPoint[]);
  const uniqueDates = Array.from(new Set(mergedData.map(point => point.date)));

  const [minY = 0, maxY = 0] = useMemo(
    () => d3.extent(mergedData.map(p => p.value)),
    [mergedData]
  );

  const [minX = new Date(), maxX = new Date()] = useMemo(
    () => d3.extent(mergedData.map(p => p.date)),
    [mergedData]
  );

  const xScale = useMemo(() => {
    return d3.scaleTime().domain([minX, maxX]).range([0, chartWidth]);
  }, [minX, maxX, chartWidth]);

  // ensures data points are not at the very edges of the chart
  const yPadding = 0.05 * Math.max(Math.abs(maxY), Math.abs(minY));
  const yScale = useMemo(() => {
    return d3
      .scaleLinear()
      .domain([minY - yPadding, maxY + yPadding])
      .range([chartHeight, 0]);
  }, [minY, maxY, h]);

  useEffect(() => {
    if (!canvasRef.current) {
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return;
    }

    if (!thresholdData?.length || !fillColors) {
      ctx.clearRect(0, 0, chartWidth, chartHeight);
      return;
    }

    drawThresholdFill(
      ctx,
      thresholdData,
      xScale,
      yScale,
      chartWidth,
      chartHeight,
      THRESHOLD,
      fillColors
    );
  }, [
    thresholdData,
    xScale,
    yScale,
    chartWidth,
    chartHeight,
    canvasRef.current,
    fillColors,
  ]);

  return (
    <div className={styles.multiLineChartRoot}>
      {tooltipData && (
        <MultiLineChartTooltip
          lines={lines}
          tooltipData={tooltipData}
          formatter={MultiLineChartFormatters[tooltipFormatType]}
          sortBy={tooltipSort}
          notAvailableLabel={notAvailableLabel}
          locale={locale}
          isBreakdown={showBreakdown}
        />
      )}
      <div
        className={styles.multiLineChartContainer}
        ref={containerRef as React.RefObject<HTMLDivElement>}
      >
        {w && h && (
          <canvas
            className={styles.canvasOverlay}
            ref={canvasRef}
            width={chartWidth}
            height={chartHeight}
            style={{ top: MARGIN.top, left: MARGIN.left }}
          />
        )}
        <svg
          className={styles.plot}
          data-testid="multi-line-chart"
          data-multi-line-chart-plot
        >
          <g transform={`translate(${MARGIN.left}, ${MARGIN.top})`}>
            <MultiLineChartAxes
              xScale={xScale}
              yScale={yScale}
              yAxisFormatter={MultiLineChartFormatters[yAxisFormatType]}
            />
            {tooltipData && (
              <line
                className={styles.tooltipLine}
                data-testid="multi-line-chart-tooltip-line"
                x1={tooltipData.xPos}
                x2={tooltipData.xPos}
                y1={0}
                y2={chartHeight}
              />
            )}
            {w &&
              h &&
              lines.map(line => (
                <MultiLineChartLinePlot
                  key={line.label}
                  xScale={xScale}
                  yScale={yScale}
                  data={line.data}
                  color={line.color}
                  dashed={line.isBenchmark}
                  tooltipDate={tooltipData?.date}
                />
              ))}
            {hasTooltip && (
              <MultiLineChartOverlay
                width={chartWidth}
                height={chartHeight}
                xScale={xScale}
                dates={uniqueDates}
                setTooltipData={setTooltipData}
              />
            )}
          </g>
        </svg>
      </div>
      {hasLegend && <MultiLineChartLegend lines={lines} />}
    </div>
  );
};
